import { useState, useEffect } from "react";
import type { LayoutSection } from "./LayoutSections";
import { ImagePlacementList } from "./ImagePlacementList";
import { api } from "../../lib/api";

interface Illustration {
  id: string;
  title: string;
  storage_path: string;
}

interface AdditionalConfig {
  link?: string;
  isFeatureImage: boolean;
}

export interface ImagePlacement {
  id: number;
  image_id: string;
  section_id: number;
  sort_order: number;
  additional_config: AdditionalConfig;
  created_at: string;
  illustration: Illustration;
  layout_section: LayoutSection;
}

type RawPlacement = {
  id: number;
  image_id: string;
  section_id: number;
  sort_order: number;
  additional_config: string | AdditionalConfig | null;
  created_at: string;
};

function parseConfig(raw: string | AdditionalConfig | null): AdditionalConfig {
  if (!raw) return { isFeatureImage: false };
  if (typeof raw !== "string") return raw;
  try {
    const parsed = JSON.parse(raw) as Partial<AdditionalConfig>;
    return { isFeatureImage: Boolean(parsed.isFeatureImage), ...parsed };
  } catch {
    return { isFeatureImage: false };
  }
}

export function ImagePlacementManager() {
  const [placements, setPlacements] = useState<ImagePlacement[]>([]);
  const [sections, setSections] = useState<LayoutSection[]>([]);
  const [illustrations, setIllustrations] = useState<Illustration[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newPlacement, setNewPlacement] = useState({
    image_id: "",
    section_id: 0,
    sort_order: 0,
    additional_config: {
      link: "",
      isFeatureImage: false,
    },
  });

  const fetchData = async () => {
    try {
      const [sectionsData, illustrationsData, placementsData] = await Promise.all([
        api.getLayoutSections(),
        api.getIllustrations(),
        api.getImagePlacements(),
      ]);

      setSections(sectionsData);

      type IllustrationRow = { id: string; title: string; storage_path: string };
      const illustrationsSlim: Illustration[] = (illustrationsData as IllustrationRow[])
        .map((i) => ({ id: i.id, title: i.title, storage_path: i.storage_path }))
        .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
      setIllustrations(illustrationsSlim);

      const illustrationMap = new Map(
        (illustrationsData as IllustrationRow[]).map((i) => [i.id, i])
      );
      const sectionMap = new Map((sectionsData as LayoutSection[]).map((s) => [s.id, s]));

      const merged: ImagePlacement[] = (placementsData as RawPlacement[])
        .map((p) => ({
          id: p.id,
          image_id: p.image_id,
          section_id: p.section_id,
          sort_order: p.sort_order,
          additional_config: parseConfig(p.additional_config),
          created_at: p.created_at,
          illustration: (illustrationMap.get(p.image_id) as Illustration) || {
            id: p.image_id,
            title: "(not found)",
            storage_path: "",
          },
          layout_section: (sectionMap.get(p.section_id) as LayoutSection) || {
            id: p.section_id,
            section_name: "(not found)",
            description: "",
            max_images: 1,
            allowed_aspect_ratios: "[]",
          },
        }))
        .sort((a, b) => a.section_id - b.section_id || a.sort_order - b.sort_order);
      setPlacements(merged);
    } catch (err) {
      if (err instanceof Error) setError(err.message);
    }
  };

  const handleSectionChange = (sectionId: number) => {
    if (!sectionId) {
      setNewPlacement({
        ...newPlacement,
        section_id: 0,
        sort_order: 0,
        additional_config: { link: "", isFeatureImage: false },
      });
      return;
    }

    const targetSection = sections.find((s) => s.id === sectionId);
    if (!targetSection) return;

    const sectionPlacements = placements.filter((p) => p.section_id === sectionId);
    if (sectionPlacements.length >= targetSection.max_images) {
      setError(
        `このセクションには既に${targetSection.max_images}枚の画像が配置されています。新しく配置するには、既存の画像を削除してください。`
      );
      setTimeout(() => setError(null), 2000);

      setNewPlacement({
        ...newPlacement,
        section_id: 0,
        sort_order: 0,
        additional_config: { link: "", isFeatureImage: false },
      });
      return;
    }

    const availableOrders = getAvailableOrders(sectionId);
    setNewPlacement({
      ...newPlacement,
      section_id: sectionId,
      sort_order: availableOrders[0] || 0,
    });
  };

  const getAvailableOrders = (sectionId: number): number[] => {
    if (!sectionId) return [];

    const targetSection = sections.find((s) => s.id === sectionId);
    if (!targetSection) return [];

    const sectionPlacements = placements.filter((p) => p.section_id === sectionId);
    const usedOrders = new Set(sectionPlacements.map((p) => p.sort_order));

    return Array.from({ length: targetSection.max_images }, (_, i) => i).filter(
      (order) => !usedOrders.has(order)
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const targetSection = sections.find((s) => s.id === newPlacement.section_id);
      if (!targetSection) throw new Error("セクションが見つかりません");

      const currentCount = placements.filter(
        (p) => p.section_id === newPlacement.section_id
      ).length;
      if (currentCount >= targetSection.max_images) {
        throw new Error(
          `このセクションには最大${targetSection.max_images}枚までしか配置できません`
        );
      }

      await api.createImagePlacement({
        image_id: newPlacement.image_id,
        section_id: newPlacement.section_id,
        sort_order: newPlacement.sort_order,
        additional_config: newPlacement.additional_config,
      });

      setSuccess("画像を配置しました");
      setTimeout(() => setSuccess(null), 3000);

      setNewPlacement({
        image_id: "",
        section_id: 0,
        sort_order: 0,
        additional_config: { link: "", isFeatureImage: false },
      });

      fetchData();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("この配置を削除してもよろしいですか？")) return;

    try {
      await api.deleteImagePlacement(id);
      setSuccess("配置を削除しました");
      setTimeout(() => setSuccess(null), 3000);
      fetchData();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="p-4">
      <div className="space-y-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="border rounded-md p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                セクション <span className="text-red-500">*</span>
              </label>
              <select
                value={newPlacement.section_id || ""}
                onChange={(e) => handleSectionChange(parseInt(e.target.value))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              >
                <option value="">選択してください</option>
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.section_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                イラスト <span className="text-red-500">*</span>
              </label>
              <select
                value={newPlacement.image_id}
                onChange={(e) =>
                  setNewPlacement({ ...newPlacement, image_id: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              >
                <option value="">選択してください</option>
                {illustrations.map((illustration) => (
                  <option key={illustration.id} value={illustration.id}>
                    {illustration.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                表示順序 <span className="text-red-500">*</span>
              </label>
              <select
                value={newPlacement.sort_order}
                onChange={(e) =>
                  setNewPlacement({
                    ...newPlacement,
                    sort_order: parseInt(e.target.value),
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
                disabled={!newPlacement.section_id}
              >
                {getAvailableOrders(newPlacement.section_id).map((order) => (
                  <option key={order} value={order}>
                    {order}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">リンク</label>
              <input
                type="text"
                value={newPlacement.additional_config.link}
                onChange={(e) =>
                  setNewPlacement({
                    ...newPlacement,
                    additional_config: {
                      ...newPlacement.additional_config,
                      link: e.target.value,
                    },
                  })
                }
                placeholder="https://example.com"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                サイズ大
              </label>
              <input
                type="checkbox"
                checked={newPlacement.additional_config.isFeatureImage}
                onChange={(e) =>
                  setNewPlacement({
                    ...newPlacement,
                    additional_config: {
                      ...newPlacement.additional_config,
                      isFeatureImage: e.target.checked,
                    },
                  })
                }
                className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
            </div>
          </div>

          <button
            type="submit"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            画像・アイコンを追加
          </button>
        </form>

        <ImagePlacementList
          sections={sections}
          placements={placements}
          onDelete={handleDelete}
          onUpdate={fetchData}
        />
      </div>
    </div>
  );
}
